import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactI18n from 'react-i18n';
import {
  Box,
  CallToActionButton,
  CallToActionLink,
  FlexBox,
  Panel,
  Divider,
  TextCaption,
  TextBody,
  TextDense,
  TextH1,
  TextH2,
  Pill,
  Spacer,
  IconFontAwesome
} from '@directsoftware/ui-kit-web-admin';
import { formatCurrency } from '../../../../shared/helpers';
import {
  nightlyRateKey,
  paymentActivity,
  paymentTerms,
  paymentDueToday,
  showDeposit,
  depositAmount
} from '../../../utils/receipt-redesign';
import chargeDescription from '../../../utils/charge-description';
import { guestBalance, formatPaymentDate } from '../../../utils/payment-terms';
import BalanceDueCta from './balance-due-cta';
import { Progress, depositDescription, date } from './redesign-cards';
import useIsPrinting from './use-is-printing';
import DownloadPdfButton from './download-pdf-button';

const t = (key, values) =>
  ReactI18n.getIntlMessage(`cx.receipt.redesign.${key}`, values);

const PaymentSummary = ({
  receipt,
  brand,
  paymentState,
  depositState,
  stayLoading,
  lifecycle,
  channelPaid
}) => {
  const [showTaxes, setShowTaxes] = useState(false);
  const [showFees, setShowFees] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const isPrinting = useIsPrinting();
  const pricing = receipt.pricing || {};
  const fees = Array.isArray(pricing.fees) ? pricing.fees : [];
  const taxes = Array.isArray(pricing.tax_line_items)
    ? pricing.tax_line_items
    : [];
  const discounts = Array.isArray(pricing.discount) ? pricing.discount : [];
  const discountTotal =
    discounts.reduce((sum, item) => sum + Number(item.total_cents || 0), 0) /
    100;
  const currency = amount =>
    formatCurrency(Number(amount || 0), brand.currency);
  const { total, paid, remaining, overpaid } = guestBalance(receipt.booking);
  const feeTotal =
    fees
      .filter(fee => !(fee.additional_data || {}).included_in_base_rent)
      .reduce((sum, fee) => sum + Number(fee.total_cents || 0), 0) / 100;
  const { charges, refunded } = paymentActivity({
    charges: receipt.charges,
    refunds: receipt.refunds,
    pricePaid: receipt.booking.price_paid
  });
  const terms = paymentTerms(receipt, paymentState);
  const dueDate = formatPaymentDate(terms.dueDate, brand.date_format);
  const closed = ['completed', 'cancelled', 'declined', 'expired'].includes(
    lifecycle
  );
  const dueToday = paymentDueToday(
    receipt,
    paymentState,
    lifecycle,
    stayLoading
  );
  let balanceStatus = 'balance_pending';
  let balanceColor = 'gray';
  let balanceCaption = '';
  if (closed) {
    balanceStatus = 'nothing_owed';
    balanceCaption = t('reservation_closed');
  } else if (remaining <= 0) {
    balanceStatus = 'paid_in_full';
    balanceColor = 'green';
    balanceCaption = t('reservation_paid');
  } else if (dueToday) {
    balanceStatus = 'due_today';
    balanceColor = 'yellow';
    balanceCaption = t('payment_due_now');
  } else if (paymentState && paymentState.status === 'scheduled') {
    balanceStatus = 'status_scheduled';
    balanceCaption = dueDate
      ? t('automatic_on_date', { date: dueDate })
      : t('automatic_later');
  } else if (paymentState && paymentState.status === 'due_later') {
    balanceStatus = 'due_later';
    balanceCaption = dueDate ? t('pay_by_date', { date: dueDate }) : '';
  } else if (!paymentState && terms.scheduled) {
    balanceStatus = terms.autoCollected ? 'status_scheduled' : 'due_later';
    if (terms.autoCollected)
      balanceCaption = dueDate
        ? t('automatic_on_date', { date: dueDate })
        : t('automatic_later');
    else if (dueDate) balanceCaption = t('pay_by_date', { date: dueDate });
  }
  let deposit =
    showDeposit(receipt, depositState) &&
    depositDescription(receipt, brand.currency, depositState, stayLoading);
  if (
    closed &&
    deposit &&
    ['action_needed', 'scheduled'].includes(deposit.status)
  )
    deposit = null;
  let depositColor = 'gray';
  let depositKind = 'deposit_kind';
  if (deposit) {
    if (deposit.status === 'action_needed') depositColor = 'yellow';
    if (
      ['authorized', 'charged', 'collected', 'complete'].includes(
        deposit.status
      )
    )
      depositColor = 'green';
    if (['action_needed', 'scheduled', 'authorized'].includes(deposit.status))
      depositKind = 'deposit_kind_hold';
    else if (deposit.status === 'charged') depositKind = 'deposit_kind_charged';
    else if (deposit.status === 'collected')
      depositKind = 'deposit_kind_covered';
  }
  const row = (label, amount, key) => (
    <FlexBox
      justifyContent="space-between"
      alignItems="flex-start"
      gap="s"
      paddingVertical={key === 'total' ? undefined : 'xxs'}
      className={key === 'total' ? 'receiptRedesign__totalDivider' : undefined}
      key={key || label}
    >
      <TextBody renderAs="dt">{label}</TextBody>
      <TextBody renderAs="dd">{amount}</TextBody>
    </FlexBox>
  );
  const breakdownRow = (label, amount, key) => (
    <FlexBox
      justifyContent="space-between"
      alignItems="flex-start"
      gap="s"
      key={key}
    >
      <TextDense weight="regular" renderAs="dt">
        {label}
      </TextDense>
      <TextDense weight="regular" renderAs="dd">
        {amount}
      </TextDense>
    </FlexBox>
  );
  return (
    <Panel>
      <Box padding="m">
        <aside aria-labelledby="payment-heading">
          <TextCaption
            className="receiptRedesign__eyebrow"
            textColor="light-gray"
            textTransform="uppercase"
            isFullWidth
          >
            {t('your_receipt')}
          </TextCaption>
          <TextH2 renderAs="h2" id="payment-heading">
            {t('payment_summary')}
          </TextH2>
          <Spacer size="xs" />
          <dl className="receiptRedesign__lineItems">
            {receipt.pricing && (
              <>
                {row(
                  t(nightlyRateKey(receipt.nights), {
                    nights: receipt.nights,
                    rate: currency(pricing.average_nightly_price)
                  }),
                  currency(pricing.room_rate)
                )}
                {fees
                  .filter(
                    fee =>
                      (fee.additional_data || {}).included_in_base_rent &&
                      Number(fee.total_cents) > 0
                  )
                  .map((fee, index) =>
                    row(
                      `${fee.name} ${ReactI18n.getIntlMessage(
                        'cx.global.included_in_rate'
                      )}`,
                      currency(fee.total_cents / 100),
                      `included-${index}`
                    )
                  )}
                {discounts
                  .filter(discount => Number(discount.total_cents) > 0)
                  .map((discount, index) =>
                    row(
                      `${
                        discount.name &&
                        discount.name.toLowerCase().includes('discount')
                          ? discount.name
                          : `${t('discount')}${
                              discount.name ? `: ${discount.name}` : ''
                            }`
                      }${discount.rate > 0 ? ` (${discount.rate}%)` : ''}`,
                      `−${currency(discount.total_cents / 100)}`,
                      `discount-${index}`
                    )
                  )}
                {row(
                  <FlexBox alignItems="center" gap="xs">
                    <TextBody>{t('taxes')}</TextBody>
                    {taxes.length > 0 && (
                      <button
                        type="button"
                        className="receiptRedesign__disclosure"
                        aria-label={t('taxes')}
                        aria-expanded={showTaxes}
                        aria-controls="receipt-tax-details"
                        onClick={() => setShowTaxes(!showTaxes)}
                      >
                        <span
                          aria-hidden="true"
                          className="receiptRedesign__chevron"
                        />
                      </button>
                    )}
                  </FlexBox>,
                  currency(pricing.taxes),
                  'taxes'
                )}
                <div
                  id="receipt-tax-details"
                  hidden={!showTaxes && !isPrinting}
                  className={
                    showTaxes || isPrinting
                      ? 'receiptRedesign__breakdown'
                      : undefined
                  }
                >
                  {(showTaxes || isPrinting) &&
                    taxes.map((tax, index) =>
                      breakdownRow(
                        tax.name || t('taxes'),
                        currency(tax.total_cents / 100),
                        `tax-${index}`
                      )
                    )}
                </div>
                {row(
                  <FlexBox alignItems="center" gap="xs">
                    <TextBody>{t('fees')}</TextBody>
                    {fees.some(
                      fee =>
                        !(fee.additional_data || {}).included_in_base_rent &&
                        Number(fee.total_cents) > 0
                    ) && (
                      <button
                        type="button"
                        className="receiptRedesign__disclosure"
                        aria-label={t('fees')}
                        aria-expanded={showFees}
                        aria-controls="receipt-fee-details"
                        onClick={() => setShowFees(!showFees)}
                      >
                        <span
                          aria-hidden="true"
                          className="receiptRedesign__chevron"
                        />
                      </button>
                    )}
                  </FlexBox>,
                  currency(feeTotal),
                  'fees'
                )}
                <div
                  id="receipt-fee-details"
                  hidden={!showFees && !isPrinting}
                  className={
                    showFees || isPrinting
                      ? 'receiptRedesign__breakdown'
                      : undefined
                  }
                >
                  {(showFees || isPrinting) &&
                    fees
                      .filter(
                        fee =>
                          !(fee.additional_data || {}).included_in_base_rent &&
                          Number(fee.total_cents) > 0
                      )
                      .map((fee, index) =>
                        breakdownRow(
                          fee.name,
                          currency(fee.total_cents / 100),
                          `fee-${index}`
                        )
                      )}
                </div>
              </>
            )}
            {row(
              <TextBody weight="bold" textColor="dark-gray">
                {t('reservation_total')}
              </TextBody>,
              <TextBody weight="bold" textColor="dark-gray">
                {currency(total)}
              </TextBody>,
              'total'
            )}
            {discountTotal > 0 &&
              row(
                ReactI18n.getIntlMessage('cx.global.you_saved'),
                currency(discountTotal),
                'saved'
              )}
          </dl>
          {!channelPaid && (
            <div className="receiptRedesign__balance">
              <div className="receiptRedesign__balanceHeading">
                <TextBody>
                  {t(closed ? 'balance' : 'remaining_balance')}
                </TextBody>
                <Pill color={balanceColor}>{t(balanceStatus)}</Pill>
              </div>
              <div className="receiptRedesign__balanceBody">
                <TextH1
                  renderAs="span"
                  weight="bold"
                  textColor="dark-gray"
                  isFullWidth
                >
                  {currency(closed ? 0 : remaining)}
                </TextH1>
                {balanceCaption && (
                  <TextCaption isFullWidth weight="regular">
                    {balanceCaption}
                  </TextCaption>
                )}
                {overpaid > 0 && (
                  <TextCaption isFullWidth weight="regular">
                    {t('overpaid_by', { amount: currency(overpaid) })}
                  </TextCaption>
                )}
                <Progress
                  value={Math.min(Math.max(paid, 0), Math.max(total, 0))}
                  max={Math.max(total, 0)}
                  label={t('payment_progress')}
                  valueText={t('paid_progress', {
                    paid: currency(paid),
                    total: currency(total)
                  })}
                />
                <Spacer size="xxs" />
                <FlexBox justifyContent="space-between" gap="s">
                  <TextCaption weight="regular">
                    {t('paid_amount', { amount: currency(paid) })}
                  </TextCaption>
                  <TextCaption weight="regular">
                    {t('of_amount', { amount: currency(total) })}
                  </TextCaption>
                </FlexBox>
                {dueToday && (
                  <BalanceDueCta
                    paymentState={paymentState}
                    isResolving={stayLoading}
                    isFullWidth
                  />
                )}
              </div>
            </div>
          )}
          {!channelPaid && (
            <>
              <TextBody renderAs="h3" weight="semibold">
                {t('payment_activity')}
              </TextBody>
              <Spacer size="xs" />
              <div id="receipt-payment-activity">
                {charges.length === 0 && refunded <= 0 && (
                  <TextBody>{t('no_payments')}</TextBody>
                )}
                {(showAll || isPrinting ? charges : charges.slice(0, 3)).map(
                  (charge, index) => (
                    <FlexBox
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap="s"
                      paddingVertical="xs"
                      key={`charge-${charge.id || index}`}
                    >
                      <FlexBox gap="xs" alignItems="flex-start">
                        <span
                          className="receiptRedesign__activityIcon"
                          aria-hidden="true"
                        >
                          <IconFontAwesome
                            name={
                              chargeDescription.isCardCharge(charge)
                                ? 'creditCard'
                                : 'moneyBill'
                            }
                            iconStyle="regular"
                          />
                        </span>
                        <div>
                          <TextDense
                            isFullWidth
                            weight="semibold"
                            textColor="dark-gray"
                          >
                            {t('requirement_payment')}
                          </TextDense>
                          <TextCaption isFullWidth weight="regular">
                            {[
                              date(charge.created_at),
                              chargeDescription(
                                charge,
                                ReactI18n.getIntlMessage
                              )
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </TextCaption>
                        </div>
                      </FlexBox>
                      <TextDense weight="semibold" textColor="dark-gray">
                        {currency(charge.amount_charged)}
                      </TextDense>
                    </FlexBox>
                  )
                )}
                {refunded > 0 && (
                  <FlexBox
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap="s"
                    paddingVertical="xs"
                  >
                    <FlexBox gap="xs" alignItems="center">
                      <span
                        className="receiptRedesign__activityIcon"
                        aria-hidden="true"
                      >
                        <IconFontAwesome name="reply" iconStyle="regular" />
                      </span>
                      <TextDense weight="semibold" textColor="dark-gray">
                        {ReactI18n.getIntlMessage('cx.receipt.refunded')}
                      </TextDense>
                    </FlexBox>
                    <TextDense
                      weight="semibold"
                      className="receiptRedesign__refundAmount"
                    >
                      {`−${currency(refunded)}`}
                    </TextDense>
                  </FlexBox>
                )}
              </div>
              {charges.length > 3 && (
                <button
                  type="button"
                  className="receiptRedesign__link"
                  aria-expanded={showAll}
                  aria-controls="receipt-payment-activity"
                  onClick={() => setShowAll(!showAll)}
                >
                  <TextBody weight="semibold">
                    {showAll
                      ? t('show_less')
                      : t('show_all', { count: charges.length })}
                  </TextBody>
                </button>
              )}
            </>
          )}
          {deposit && (
            <>
              <Divider padding="s" />
              <FlexBox justifyContent="space-between">
                <TextBody renderAs="h3" weight="semibold">
                  {t('requirement_security_deposit')}
                </TextBody>
                <Pill color={depositColor}>
                  {t(`status_${deposit.status}`)}
                </Pill>
              </FlexBox>
              <Spacer size="xs" />
              <FlexBox justifyContent="space-between">
                <TextBody textColor="dark-gray" weight="semibold">
                  {t(depositKind)}
                </TextBody>
                {depositAmount(receipt, depositState) != null && (
                  <TextBody textColor="dark-gray" weight="semibold">
                    {currency(depositAmount(receipt, depositState))}
                  </TextBody>
                )}
              </FlexBox>
              <Spacer size="xs" />
              {deposit.status === 'action_needed' && (
                <>
                  <TextCaption isFullWidth weight="regular">
                    {t(
                      depositState && depositState.status === 'failed'
                        ? 'deposit_action_summary'
                        : 'deposit_action_required'
                    )}
                  </TextCaption>
                  <Spacer size="xs" />
                </>
              )}
              {deposit.description && (
                <>
                  <TextCaption isFullWidth weight="regular">
                    {deposit.description}
                  </TextCaption>
                  <Spacer size="xs" />
                </>
              )}
              {['scheduled', 'authorized'].includes(deposit.status) && (
                <>
                  <FlexBox gap="xs" alignItems="flex-start">
                    <span aria-hidden="true">
                      <IconFontAwesome name="infoCircle" iconStyle="regular" />
                    </span>
                    <TextCaption weight="regular">
                      {t('hold_explanation')}
                    </TextCaption>
                  </FlexBox>
                  <Spacer size="xs" />
                </>
              )}
            </>
          )}
          {deposit &&
            deposit.status === 'action_needed' &&
            !stayLoading &&
            receipt.booking.confirmed &&
            !receipt.booking.cancelled &&
            depositState &&
            depositState.guestActionRequired && (
              <div className="receiptRedesign__depositAction">
                <CallToActionLink
                  href={`/my-bookings/payment/${receipt.booking.booking_code}`}
                  appearance="outline"
                  variation="secondary"
                  isFullWidth
                >
                  {t('complete_security_deposit')}
                </CallToActionLink>
              </div>
            )}
          <div className="receiptRedesign__documentActions">
            {receipt.booking.booking_code && (
              <Box flex="1">
                <DownloadPdfButton
                  brand={brand}
                  bookingCode={receipt.booking.booking_code}
                />
              </Box>
            )}
            <CallToActionButton
              onClick={() => window.print()}
              variation="secondary"
              appearance="outline"
              prependIcon={<IconFontAwesome name="print" iconStyle="regular" />}
            >
              {ReactI18n.getIntlMessage('global.actions.print.plain')}
            </CallToActionButton>
          </div>
        </aside>
      </Box>
    </Panel>
  );
};

PaymentSummary.propTypes = {
  receipt: PropTypes.object.isRequired,
  brand: PropTypes.object.isRequired,
  paymentState: PropTypes.object,
  depositState: PropTypes.object,
  stayLoading: PropTypes.bool,
  lifecycle: PropTypes.string,
  channelPaid: PropTypes.bool
};
PaymentSummary.defaultProps = {
  paymentState: null,
  depositState: null,
  stayLoading: false,
  lifecycle: null,
  channelPaid: false
};

export default PaymentSummary;
